import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Empty, Segmented, Skeleton, Table, Tag, Tooltip } from 'antd';
import {
  AlertOutlined,
  DollarOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  RedoOutlined,
  StopOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { callCenterReportsApi } from '../../utils/api';

const MIN_CALL_OPTIONS = [
  { label: '۲+ تماس', value: 2 },
  { label: '۳+ تماس', value: 3 },
  { label: '۵+ تماس', value: 5 },
];

const SIGNALS = {
  unresolved: {
    label: 'بی‌پاسخ‌مانده',
    color: 'red',
    icon: <StopOutlined />,
    hint: 'حداقل یکی از تماس‌های این مشتری بی‌پاسخ مانده است',
  },
  repeat_issue: {
    label: 'احتمال حل‌نشدن مشکل',
    color: 'orange',
    icon: <RedoOutlined />,
    hint: 'چند تماس پاسخ‌داده‌شده بدون اتصال به معامله؛ نشانه نارضایتی یا مشکل حل‌نشده',
  },
  multi_agent: {
    label: 'چند اپراتور',
    color: 'purple',
    icon: <TeamOutlined />,
    hint: 'مشتری با چند اپراتور مختلف صحبت کرده است',
  },
  sales_opportunity: {
    label: 'فرصت فروش',
    color: 'green',
    icon: <DollarOutlined />,
    hint: 'مشتری/سرنخ شناخته‌شده که هنوز معامله‌ای ندارد',
  },
};

const HISTOGRAM_LABELS = { le3: 'تا ۳ تماس', '4-5': '۴ تا ۵ تماس', '6-9': '۶ تا ۹ تماس', '10+': '۱۰ تماس یا بیشتر' };
const HISTOGRAM_COLORS = { le3: '#f59e0b', '4-5': '#f97316', '6-9': '#ea580c', '10+': '#dc2626' };

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));
const fmtDuration = (sec) => {
  const s = Math.round(Number(sec) || 0);
  if (s <= 0) return '—';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const SummaryTile = ({ icon, color, label, value, sub }) => (
  <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-4 flex items-center gap-3 min-w-0">
    <span className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0" style={{ background: `${color}1f`, color }}>
      {icon}
    </span>
    <div className="min-w-0">
      <div className="custom-font text-xs text-gray-500 dark:text-gray-400 truncate">{label}</div>
      <div className="custom-font text-xl font-bold text-gray-900 dark:text-white leading-tight" dir="ltr" style={{ textAlign: 'right' }}>
        {value}
      </div>
      {sub && <div className="custom-font text-[11px] text-gray-400 truncate">{sub}</div>}
    </div>
  </div>
);

const RepeatCallersSection = ({ from, to }) => {
  const navigate = useNavigate();
  const [minCalls, setMinCalls] = useState(3);
  const [signalFilter, setSignalFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      const res = await callCenterReportsApi.getRepeatCallers({ from, to, minCalls, limit: 100 });
      setData(res?.data ?? null);
    } catch (err) {
      setError(err?.message || 'خطا در دریافت مشتریان پرتماس');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, minCalls]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const summary = data?.summary;
  const customers = (data?.customers || []).filter((c) => !signalFilter || c.signals.includes(signalFilter));
  const histogramTotal = Object.values(data?.histogram || {}).reduce((a, b) => a + b, 0);

  const columns = [
    {
      title: 'مشتری',
      key: 'customer',
      render: (_, row) => (
        <div className="min-w-0">
          <div className="custom-font text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
            {row.name || 'مشتری ناشناس'}
            {row.type && (
              <Tag color={row.type === 'client' ? 'green' : 'orange'} className="custom-font mr-2 text-[10px] px-1 py-0">
                {row.type === 'client' ? 'مشتری' : 'سرنخ'}
              </Tag>
            )}
          </div>
          <div className="custom-font text-xs text-gray-500" dir="ltr" style={{ textAlign: 'right' }}>
            <PhoneOutlined className="ml-1" />
            {row.phone}
          </div>
        </div>
      ),
    },
    {
      title: 'تعداد تماس',
      dataIndex: 'calls',
      key: 'calls',
      sorter: (a, b) => a.calls - b.calls,
      defaultSortOrder: 'descend',
      width: 150,
      render: (calls, row) => {
        const pctAnswered = calls > 0 ? (row.answered / calls) * 100 : 0;
        return (
          <Tooltip title={<span className="custom-font">پاسخ‌داده‌شده: {row.answered} | بی‌پاسخ: {row.missed}</span>}>
            <div className="min-w-[110px]">
              <div className="custom-font text-base font-bold text-gray-900 dark:text-white">{fmt(calls)}</div>
              <div className="h-1.5 rounded-full bg-red-200 dark:bg-red-900/50 overflow-hidden" dir="ltr">
                <div className="h-full bg-emerald-500" style={{ width: `${pctAnswered}%` }} />
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'نشانه‌ها',
      key: 'signals',
      render: (_, row) =>
        row.signals.length === 0 ? (
          <span className="text-gray-300">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.signals.map((s) => (
              <Tooltip key={s} title={<span className="custom-font">{SIGNALS[s].hint}</span>}>
                <Tag color={SIGNALS[s].color} icon={SIGNALS[s].icon} className="custom-font m-0 text-xs">
                  {SIGNALS[s].label}
                </Tag>
              </Tooltip>
            ))}
          </div>
        ),
    },
    {
      title: 'آخرین موضوع / خلاصه',
      key: 'last',
      render: (_, row) => (
        <div className="min-w-0 max-w-[260px]">
          {row.last_subject && (
            <Tag color={row.last_subject.color || 'default'} className="custom-font text-xs">
              {row.last_subject.name}
            </Tag>
          )}
          {row.last_summary ? (
            <Tooltip title={<span className="custom-font leading-6">{row.last_summary}</span>}>
              <div className="custom-font text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-5">{row.last_summary}</div>
            </Tooltip>
          ) : (
            !row.last_subject && <span className="text-gray-300">—</span>
          )}
        </div>
      ),
    },
    {
      title: 'آخرین تماس',
      key: 'last_at',
      width: 150,
      render: (_, row) => (
        <div className="custom-font text-xs">
          <div dir="ltr" style={{ textAlign: 'right' }}>{row.last_at?.slice(0, 16)}</div>
          <div className="text-gray-400">اپراتور: {row.last_agent || '—'}</div>
          <div className="text-gray-400">مکالمه: <span dir="ltr">{fmtDuration(row.talk_seconds)}</span></div>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, row) =>
        row.last_call_id ? (
          <Tooltip title={<span className="custom-font">مشاهده آخرین تماس</span>}>
            <Button type="text" icon={<EyeOutlined />} onClick={() => navigate(`/call-center/calls/${row.last_call_id}`)} />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="custom-font text-lg font-bold text-gray-800 dark:text-gray-100 m-0 flex items-center gap-2">
            <AlertOutlined className="text-orange-500" />
            مشتریان پرتماس
          </h2>
          <div className="custom-font text-xs text-gray-500 dark:text-gray-400">
            مشتریانی که در این بازه چندبار تماس گرفته‌اند؛ نشانه‌ای از نارضایتی، مشکل حل‌نشده یا فرصت فروش
          </div>
        </div>
        <Segmented value={minCalls} options={MIN_CALL_OPTIONS} onChange={setMinCalls} className="custom-font" />
      </div>

      {error && <Alert type="error" showIcon message={<span className="custom-font">{error}</span>} />}

      {loading && !data ? (
        <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-4">
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      ) : data && summary.customers === 0 ? (
        <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-8">
          <Empty description={<span className="custom-font">مشتری با {minCalls} تماس یا بیشتر در این بازه وجود ندارد</span>} />
        </div>
      ) : data ? (
        <div className={`space-y-3 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryTile
              icon={<TeamOutlined />}
              color="#ea580c"
              label={`مشتریان با ${fmt(minCalls)}+ تماس`}
              value={fmt(summary.customers)}
              sub={`بیشترین: ${fmt(summary.max_calls)} تماس`}
            />
            <SummaryTile
              icon={<PhoneOutlined />}
              color="#2563eb"
              label="مجموع تماس این مشتریان"
              value={fmt(summary.calls)}
              sub={summary.share_of_incoming != null ? `${summary.share_of_incoming}% از کل تماس‌های ورودی` : undefined}
            />
            <SummaryTile
              icon={<StopOutlined />}
              color="#dc2626"
              label="دارای تماس بی‌پاسخ"
              value={fmt(summary.unresolved_customers)}
              sub="نیازمند پیگیری فوری"
            />
            <SummaryTile
              icon={<DollarOutlined />}
              color="#059669"
              label="فرصت فروش"
              value={fmt(data.signal_counts?.sales_opportunity)}
              sub="شناخته‌شده و بدون معامله"
            />
          </div>

          <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-4 space-y-3">
            {/* توزیع تعداد تماس */}
            <div>
              <div className="custom-font text-xs font-medium text-gray-500 mb-1.5">توزیع مشتریان بر اساس تعداد تماس</div>
              <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700" dir="ltr">
                {Object.entries(data.histogram).map(([k, v]) =>
                  v > 0 ? (
                    <Tooltip key={k} title={<span className="custom-font">{HISTOGRAM_LABELS[k]}: {v} مشتری</span>}>
                      <div style={{ width: `${(v / histogramTotal) * 100}%`, background: HISTOGRAM_COLORS[k] }} />
                    </Tooltip>
                  ) : null
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                {Object.entries(data.histogram).map(([k, v]) => (
                  <span key={k} className="custom-font text-[11px] text-gray-500 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: HISTOGRAM_COLORS[k] }} />
                    {HISTOGRAM_LABELS[k]}: {fmt(v)}
                  </span>
                ))}
              </div>
            </div>

            {/* فیلتر نشانه‌ها */}
            <div className="flex flex-wrap items-center gap-2">
              <Tag.CheckableTag checked={!signalFilter} onChange={() => setSignalFilter(null)} className="custom-font">
                همه ({fmt(data.shown)})
              </Tag.CheckableTag>
              {Object.entries(SIGNALS).map(([key, s]) => (
                <Tag.CheckableTag
                  key={key}
                  checked={signalFilter === key}
                  onChange={(on) => setSignalFilter(on ? key : null)}
                  className="custom-font"
                >
                  {s.icon} {s.label} ({fmt(data.signal_counts?.[key])})
                </Tag.CheckableTag>
              ))}
              <Tooltip
                title={<span className="custom-font leading-6">نشانه‌ها بر اساس رفتار تماس‌ها در همین بازه محاسبه می‌شوند و جنبه راهنما دارند، نه قطعی.</span>}
              >
                <InfoCircleOutlined className="text-gray-300 cursor-help" />
              </Tooltip>
            </div>

            <Table
              size="small"
              rowKey="phone"
              columns={columns}
              dataSource={customers}
              loading={loading}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
              locale={{ emptyText: <span className="custom-font">موردی یافت نشد</span> }}
            />
            {data.summary.customers > data.shown && (
              <div className="custom-font text-[11px] text-gray-400">
                {fmt(data.shown)} مشتری پرتماس‌تر از مجموع {fmt(data.summary.customers)} مشتری نمایش داده شده است.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RepeatCallersSection;
